import type { UserSkill } from '@compass/client/types/user';

import { WorkflowType } from '@/constants/dispositions';
import { workflowTypeLabel } from '@/constants/workflows';
import type { SelectOption } from '@/types/formSchema';

/**
 * The role a user holds for a skill, in increasing seniority:
 * - `Backup`  — fallback handler
 * - `Primary` — preferred handler
 * - `Leader`  — the single designated leader for the skill within the org
 *
 * The backend stores this as two independent booleans (`primary`, `leader`),
 * but the UI treats them as one mutually-exclusive role: a leader is always
 * also a primary handler, so the three roles map cleanly onto the flags.
 */
export enum SkillRole {
  Backup = 'backup',
  Primary = 'primary',
  Leader = 'leader',
}

/** All roles, ordered from least to most senior (drives the toggle order). */
export const SKILL_ROLES: readonly SkillRole[] = [
  SkillRole.Backup,
  SkillRole.Primary,
  SkillRole.Leader,
];

const SKILL_ROLE_LABEL: Record<SkillRole, string> = {
  [SkillRole.Backup]: 'Backup',
  [SkillRole.Primary]: 'Primary',
  [SkillRole.Leader]: 'Leader',
};

/** Derive the display role from a skill's persisted flags. Leader wins. */
export const skillRole = (skill: Pick<UserSkill, 'primary' | 'leader'>): SkillRole =>
  skill.leader ? SkillRole.Leader : skill.primary ? SkillRole.Primary : SkillRole.Backup;

/** Human-readable label for a skill role (e.g. "Leader"). */
export const skillRoleLabel = (role: SkillRole): string => SKILL_ROLE_LABEL[role];

/**
 * Map a display role back to the persisted booleans. A leader is also a primary
 * handler, so both `primary` and `leader` are true for the `leader` role.
 */
export const skillRoleFlags = (role: SkillRole): { primary: boolean; leader: boolean } => ({
  primary: role !== SkillRole.Backup,
  leader: role === SkillRole.Leader,
});

/**
 * Skills-only "audit" designations. These are variants of an existing workflow
 * (e.g. an audit role for PBM cases) surfaced in the skills picker, but they are
 * deliberately NOT `WorkflowType` members — they never represent a real case
 * workflow and must not leak into disposition maps, filters, or case creation.
 * The stored `value` is validated by the Go backend (`IsAssignableSkill`).
 */
interface AuditSkill {
  value: string;
  label: string;
}

const AUDIT_SKILLS: readonly AuditSkill[] = [
  { value: 'pbm_audit', label: 'PharmBV Audit' },
  { value: 'medbv_audit', label: 'MedBV Audit' },
  { value: 'transportation_audit', label: 'Transportation Audit' },
];

/**
 * Manual-task routing skills (JNJ-200): case-dispatch offers refill's post-call
 * manual tasks to operators holding these. They are NOT audit roles and NOT
 * `WorkflowType` members — a task skill says which tasks a user may be handed,
 * never which cases they work. Validated by the Go backend (`IsTaskSkill`).
 */
const TASK_SKILLS: readonly { value: string; label: string }[] = [
  { value: 'refill_pss', label: 'Refill PSS' },
  { value: 'refill_pharmacist', label: 'Refill Pharmacist' },
];

/**
 * Human-readable label for a skill value. Resolves audit and task skills first,
 * then falls back to `workflowTypeLabel` for workflow-type skills. Use this (not
 * `workflowTypeLabel` directly) anywhere a persisted skill value is rendered.
 */
export const skillLabel = (value?: string | null): string => {
  const named = value ? [...AUDIT_SKILLS, ...TASK_SKILLS].find(s => s.value === value) : undefined;
  return named ? named.label : workflowTypeLabel(value);
};

/**
 * Maps a workflow type to the skill that serves it, for the cases where their names differ.
 * There is a single "medbv" skill (there is no "unifiedmedbv" skill) that serves BOTH the
 * "medbv" and "unifiedmedbv" workflow types. Every other workflow type is served by a
 * like-named skill, so only the exceptions are listed. Keep in sync with the backend
 * `workflowTypeSkill` (pkg/workflows/types.go).
 */
const SKILL_FOR_WORKFLOW_TYPE: Record<string, string> = {
  [WorkflowType.UNIFIEDMEDBV]: WorkflowType.MEDBV,
  // BHO is NOT listed: like pa_status it has its own like-named "bho" skill (BV-2476).
  // MEDBHOBV (BV-2328) reuses the medbv skill.
  [WorkflowType.MEDBHOBV]: WorkflowType.MEDBV,
};

/** The skill that serves a workflow type (identity when the names match). */
export const skillForWorkflowType = (workflowType: string): string =>
  SKILL_FOR_WORKFLOW_TYPE[workflowType] ?? workflowType;

/**
 * Whether a user holds leadership (`leader === true`) of any skill in the given
 * (org-scoped) skill set.
 *
 * Drives the "skill leader must be a team lead" rule (BI-1698), which is deliberately
 * ONE-directional: holding leader on a skill requires being the lead (`manager_id`) of a
 * team in that org, but a team lead needs no skill leadership at all. The team's
 * `workflow_types` are irrelevant to the rule — the old BI-1544 direction ("a team lead
 * must lead a skill serving one of the team's workflow types") was removed.
 */
export const hasLeaderSkill = (skills: Pick<UserSkill, 'leader'>[] | undefined): boolean =>
  (skills ?? []).some(s => s.leader);

/**
 * Build the list of skill options offered in the picker.
 *
 * Options are sourced from the known `WorkflowType` enum plus the standalone
 * `AUDIT_SKILLS` and `TASK_SKILLS`, labelled via `skillLabel`. Already-assigned skills are
 * excluded, and options are de-duplicated by label (e.g. MEDBV and UNIFIEDMEDBV
 * both render as "MedBV"), keeping the first matching value. Results are sorted
 * by label.
 */
export const getAvailableSkillOptions = (assignedSkills: string[]): SelectOption[] => {
  const assigned = new Set(assignedSkills);
  // Seed seen labels with assigned skills so a duplicate-labelled workflow type
  // (e.g. UNIFIEDMEDBV when MEDBV is already assigned) is not offered as "MedBV".
  const seenLabels = new Set<string>(assignedSkills.map(skill => skillLabel(skill)));
  const options: SelectOption[] = [];

  const candidates: { value: string; label: string }[] = [
    ...Object.values(WorkflowType).map(value => ({ value, label: skillLabel(value) })),
    ...AUDIT_SKILLS.map(({ value, label }) => ({ value, label })),
    ...TASK_SKILLS,
  ];

  for (const { value, label } of candidates) {
    if (assigned.has(value)) continue;
    if (seenLabels.has(label)) continue;
    seenLabels.add(label);
    options.push({ value, label });
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
};
